import { create } from 'zustand'
import type { GroupTablePermissionConfig, PermissionUser, UserGroup } from '../models'

/**
 * UI state and (temporary) mock data for the permissions feature.
 *
 * Long term, all server data — users, groups, assigned/resolved
 * permissions, action and resource-type metadata — should live in
 * TanStack Query (see `services/api/permissions.api.ts`). The pages
 * that are wired up this sprint (Users, Groups, Membership) read
 * directly from the API.
 *
 * The `userGroups`, `users`, and `tablePermissions` arrays remain here
 * only as a stopgap for components that haven't been migrated yet:
 * `TablePermissionsPanel`, `FieldPermissionsPanel`, and the Records
 * matrix tab. Migrate those to TanStack Query and delete these fields.
 */
interface PermissionsState {
  /** @deprecated Use TanStack Query (queryKeys.permissionsUsers) instead. */
  users: PermissionUser[]
  /** @deprecated Use TanStack Query (queryKeys.permissionsUserGroups) instead. */
  userGroups: UserGroup[]
  /** @deprecated Will be derived from /permissions/assigned + /permissions/actions. */
  tablePermissions: GroupTablePermissionConfig[]

  /** Expanded table rows on the permission-group Tables tab, keyed by group id. */
  expandedTableIdsByGroup: Record<string, string[]>

  toggleExpandedTable: (groupId: string, tableId: string) => void
}

export const usePermissionsStore = create<PermissionsState>((set) => ({
  users: [],
  userGroups: [],
  tablePermissions: [],

  expandedTableIdsByGroup: {},

  toggleExpandedTable: (groupId, tableId) =>
    set((state) => {
      const current = state.expandedTableIdsByGroup[groupId] ?? []
      const next = current.includes(tableId)
        ? current.filter((id) => id !== tableId)
        : [...current, tableId]

      return {
        expandedTableIdsByGroup: {
          ...state.expandedTableIdsByGroup,
          [groupId]: next,
        },
      }
    }),
}))
